// @flow

import React, { PureComponent } from 'react';
import { createPaginationContainer, graphql } from 'react-relay';
import debounce from 'lodash.debounce';
import { BrowserUtils } from 'shared/utils';
import LoadingMore from 'shared/components/common/LoadingMore';
import EmptySearchResults from 'shared/components/common/EmptySearchResults';
import ChartOfAccountsTable from './ChartOfAccountsTable';

type Props = {
  history: Object,
  filtersDirty: boolean,
  onCreateNew: () => void,
  onEdit: (account: Object) => void,
  glAccounts: Object,
  relay: {
    environment: Object,
    hasMore: () => boolean,
    isLoading: () => boolean,
    loadMore: (pageSize: number, callback: Function) => void,
  },
};

class PaginatedGLAccountsContainer extends PureComponent<Props> {
  constructor(props: Props) {
    super(props);
    this.onScrollEndReached = debounce(this.onScrollEndReached, 100);
  }

  componentDidMount = () => {
    document.addEventListener('scroll', this.onScrollEndReached);
  };

  componentWillUnmount = () => {
    document.removeEventListener('scroll', this.onScrollEndReached);
  };

  onScrollEndReached = () => {
    const { relay } = this.props;
    if (BrowserUtils.scrollEndReached(200)) {
      if (relay.hasMore()) {
        relay.loadMore(30);
      }
    }
  };

  render() {
    const { history, onCreateNew, onEdit, glAccounts, relay, filtersDirty } =
      this.props;

    if (
      (!glAccounts.glAccounts.edges ||
        !glAccounts.glAccounts.edges.length) &&
      filtersDirty
    ) {
      return <EmptySearchResults />;
    }

    return (
      <>
        <ChartOfAccountsTable
          history={history}
          onCreateNew={onCreateNew}
          onEdit={onEdit}
          glAccountEdges={glAccounts.glAccounts.edges}
        />
        {relay.hasMore() && relay.isLoading() ? <LoadingMore /> : null}
      </>
    );
  }
}

export default createPaginationContainer(
  PaginatedGLAccountsContainer,
  {
    glAccounts: graphql`
      fragment PaginatedGLAccountsContainer_glAccounts on Query
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 30 }
          cursor: { type: "String" }
          search: { type: "String" }
          sortBy: { type: "GLAccountSort" }
          accountTypes: { type: "[GLAccountType!]" }
          isActive: { type: "Boolean" }
        ) {
        glAccounts(
          first: $count
          after: $cursor
          search: $search
          sortBy: $sortBy
          accountTypes: $accountTypes
          isActive: $isActive
        )
          @connection(
            key: "PaginatedGLAccountsContainer_glAccounts"
            filters: []
          ) {
          edges {
            cursor
            ...ChartOfAccountsTable_glAccountEdges
          }
        }
      }
    `,
  },
  {
    query: graphql`
      query PaginatedGLAccountsContainerQuery(
        $count: Int!
        $cursor: String
        $search: String
        $sortBy: GLAccountSort
        $accountTypes: [GLAccountType!]
        $isActive: Boolean
      ) {
        ...PaginatedGLAccountsContainer_glAccounts
          @arguments(
            count: $count
            cursor: $cursor
            search: $search
            sortBy: $sortBy
            accountTypes: $accountTypes
            isActive: $isActive
          )
      }
    `,
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return { ...fragmentVariables, count, cursor };
    },
  },
);
